import type { JSONContent } from '@tiptap/core'
import { sanitizeUrl } from '@disco/utils/links'
import { sanitizeCssValue } from '@disco/utils/css-utils'

type MjmlSettings = {
  fontFamily?: string
  fontSize?: string
  lineHeight?: number
}

const DEFAULT_SETTINGS: MjmlSettings = {
  fontFamily: 'Helvetica, Arial, sans-serif',
  fontSize: '14px',
  lineHeight: 1.5,
}

/**
 * Converts TipTap JSON content to MJML for email rendering.
 */
export function toMjml(doc: JSONContent, settings: MjmlSettings = {}): string {
  const merged = { ...DEFAULT_SETTINGS, ...settings }
  if (!doc?.content) return ''

  const sections = doc.content.map(node => nodeToMjml(node, merged))
  return sections.join('')
}

const ALLOWED_ALIGNMENTS = new Set(['left', 'right', 'center', 'justify'])

function alignAttr(node: JSONContent): string {
  const align = node.attrs?.textAlign
  if (typeof align !== 'string' || !ALLOWED_ALIGNMENTS.has(align)) return ''
  return ` align="${align}"`
}

function nodeToMjml(node: JSONContent, settings: MjmlSettings): string {
  const fontFamily = escapeHtml(settings.fontFamily || '')
  const fontSize = escapeHtml(settings.fontSize || '')
  const lineHeight = escapeHtml(String(settings.lineHeight ?? ''))

  switch (node.type) {
    case 'paragraph':
      return `<mj-text font-family="${fontFamily}" font-size="${fontSize}" line-height="${lineHeight}"${alignAttr(
        node,
      )}>${inlineContent(node)}</mj-text>`

    case 'heading': {
      const level = Math.min(Math.max(Number(node.attrs?.level) || 1, 1), 6)
      const sizes: Record<number, string> = {
        1: '32px',
        2: '24px',
        3: '18px',
        4: '16px',
      }
      return `<mj-text font-family="${fontFamily}" font-size="${
        sizes[level] || '14px'
      }"${alignAttr(node)}><h${level}>${inlineContent(
        node,
      )}</h${level}></mj-text>`
    }

    case 'bulletList':
      return `<mj-text font-family="${fontFamily}" font-size="${fontSize}"><ul>${listItems(
        node,
      )}</ul></mj-text>`

    case 'orderedList':
      return `<mj-text font-family="${fontFamily}" font-size="${fontSize}"><ol>${listItems(
        node,
      )}</ol></mj-text>`

    case 'ctaButton': {
      const btnUrl = escapeHtml(sanitizeUrl(node.attrs?.url || ''))
      const btnBg = escapeHtml(
        sanitizeCssValue(node.attrs?.backgroundColor || '#0693e3'),
      )
      const btnColor = escapeHtml(
        sanitizeCssValue(node.attrs?.textColor || '#ffffff'),
      )
      const btnRadius = escapeHtml(
        sanitizeCssValue(node.attrs?.borderRadius || '3px'),
      )
      const btnPadding = escapeHtml(
        sanitizeCssValue(node.attrs?.padding || '10px 25px'),
      )
      const btnText = escapeHtml(node.attrs?.text || 'Click Here')
      return `<mj-button background-color="${btnBg}" color="${btnColor}" border-radius="${btnRadius}" inner-padding="${btnPadding}" href="${btnUrl}" font-family="${fontFamily}">${btnText}</mj-button>`
    }

    case 'image': {
      const imgSrc = escapeHtml(sanitizeUrl(node.attrs?.src || ''))
      const imgAlt = escapeHtml(node.attrs?.alt || '')
      const imgWidth = node.attrs?.width ? node.attrs.width + 'px' : 'auto'
      const imgHeight = node.attrs?.height
        ? escapeHtml(node.attrs.height + 'px')
        : ''
      const imgLink = node.attrs?.link
        ? escapeHtml(sanitizeUrl(node.attrs.link))
        : ''
      let mjImgAttrs = `src="${imgSrc}" alt="${imgAlt}" width="${escapeHtml(
        imgWidth,
      )}"`
      if (imgHeight) mjImgAttrs += ` height="${imgHeight}"`
      if (imgLink) mjImgAttrs += ` href="${imgLink}"`
      return `<mj-image ${mjImgAttrs} />`
    }

    default:
      return ''
  }
}

function listItems(node: JSONContent): string {
  if (!node.content) return ''
  return node.content
    .map(
      item =>
        `<li>${
          item.content
            ?.filter(child => child.type === 'paragraph')
            .map(child => inlineContent(child))
            .join('<br />') || ''
        }</li>`,
    )
    .join('')
}

function inlineContent(node: JSONContent): string {
  if (!node.content) return ''
  return node.content.map(inlineNodeToHtml).join('')
}

function inlineNodeToHtml(node: JSONContent): string {
  if (node.type === 'text') {
    let html = escapeHtml(node.text || '')
    if (node.marks) {
      for (const mark of node.marks) {
        switch (mark.type) {
          case 'bold':
            html = `<b>${html}</b>`
            break
          case 'italic':
            html = `<i>${html}</i>`
            break
          case 'underline':
            html = `<u>${html}</u>`
            break
          case 'link': {
            const safeHref = sanitizeUrl(mark.attrs?.href || '')
            html = `<a href="${escapeHtml(safeHref)}">${html}</a>`
            break
          }
          case 'textStyle': {
            const textStyles: string[] = []
            if (mark.attrs?.color)
              textStyles.push(`color: ${sanitizeCssValue(mark.attrs.color)}`)
            if (mark.attrs?.fontSize)
              textStyles.push(
                `font-size: ${sanitizeCssValue(mark.attrs.fontSize)}`,
              )
            if (textStyles.length)
              html = `<span style="${escapeHtml(
                textStyles.join('; '),
              )}">${html}</span>`
            break
          }
          case 'highlight': {
            const bgColor = sanitizeCssValue(mark.attrs?.color || '')
            if (bgColor)
              html = `<span style="background-color: ${escapeHtml(
                bgColor,
              )}">${html}</span>`
            break
          }
        }
      }
    }
    return html
  }

  if (node.type === 'mergeTag') {
    const templateVar = node.attrs?.templateVar || 'first_name'
    const fallback = node.attrs?.label || ''
    return `{{${escapeHtml(templateVar)}|${escapeHtml(fallback)}}}`
  }

  if (node.type === 'mention') {
    return `@${escapeHtml(node.attrs?.label || '')}`
  }

  if (node.type === 'hardBreak') {
    return '<br />'
  }

  return ''
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
